import { useCallback, useState } from 'react';
import { useRouter } from 'next/navigation';
import type { GameSessionFragment } from '~/client/components/Game/graphql/GameSessionFragment.generated';
import type { SingleGameFragment } from '~/client/components/Game/graphql/SingleGameFragment.generated';
import useWalletUnit from '~/client/hooks/currency/useWalletUnit';
import useAgentData from '~/client/hooks/useAgentData';
import useUniqueHashEffect from '~/client/hooks/useUniqueHashEffect';
import type StartSessionProps from './StartSessionProps';
import useGameSessionHandler from './useGameSessionHandler';
import useFunModeProps from '../useFunModeProps';
import useCashierCurrencyChange from '@/server/components/NavBar/LoggedInControls/UserControls/UserBalance/Balance/useBalance/useCashierCurrencyChange';

type Props = StartSessionProps & { game?: SingleGameFragment };

const useGameSessionProps = ({ isFunMode, game, inGameCurrency }: Props) => {
  const router = useRouter();
  const walletUnit = useWalletUnit();
  const { isDesktop } = useAgentData();
  const [gameSession, setGameSession] = useState<GameSessionFragment>();
  const [loadingSession, setLoadingSession] = useState(false);
  const { setFunMode } = useFunModeProps();
  const startGameSession = useGameSessionHandler({ game });
  const gameId = game?.id;

  const closeGameSession = () => setGameSession(undefined);

  const startSession = useCallback(
    async (props: StartSessionProps) => {
      setLoadingSession(true);
      closeGameSession();
      setFunMode(props.isFunMode);
      const session = await startGameSession(props);
      if (session) {
        setGameSession(session);
      }
      setLoadingSession(false);
    },
    [setFunMode, startGameSession],
  );

  useCashierCurrencyChange({
    onChange: () => {
      router.refresh();
    },
  });

  useUniqueHashEffect(
    () => {
      if (gameId && isDesktop) {
        startSession({ isFunMode, inGameCurrency });
      }
    },
    { gameId, isFunMode, inGameCurrency, walletUnit },
  );

  return { gameSession, startSession, closeGameSession, loadingSession };
};

export default useGameSessionProps;
